import { createSlice } from '@reduxjs/toolkit'
import gqlCaller, { gqlCallerV2 } from 'graphql/gqlcaller'
import { BillingEntity } from 'models/BillingModel'
import { CkycRecord } from 'models/ckyc.model'
import {
  MerchantAccountDetails,
  MerchantContactDetail,
  MerchantMccDetail,
} from 'models/MerchantDetailModel'
import { Opportunity } from 'models/OpprtunitiesModel'
import { PanDetailsResponse, PanGstResponse } from 'types/responsetypes'
import { consoleLog } from 'utils/Logger'

type initialStateType = {
  storeList: any
  contactList: Array<MerchantContactDetail>
  mccListOnAccount: Array<MerchantMccDetail>
  merchantContactMccList: MerchantAccountDetails
  billingList: Array<BillingEntity>
  isAccountEditable: boolean
  merchantContactList: Array<MerchantContactDetail>
  contactPageNumber: number
  hasMoreContact: boolean
  isProductAdded: boolean
  merchantAccountDetails: MerchantAccountDetails
  opportunityData: Opportunity
  panDetails: PanDetailsResponse
  ckycSearchResult: CkycRecord
  ckycGstResult: PanGstResponse
  ckycSearchCompleted: boolean
}
const initialState = {
  merchantContactMccList: null,
  merchantContactList: [],
  contactPageNumber: 0,
  hasMoreContact: true,
  contactList: [],
  billingList: [],
  isAccountEditable: false,
  isProductAdded: false,
  opportunityData: null,
  ckycSearchCompleted: false,
  merchantAccountDetails: null,
} as initialStateType

const businessSlice = createSlice({
  name: 'business',
  initialState: initialState,
  reducers: {
    setContactList: (state, action) => {
      state.contactList = action.payload
    },
    setMccListOnAccount: (state, action) => {
      state.mccListOnAccount = action.payload
    },
    setMerchantContactMcc: (state, action) => {
      state.merchantContactMccList = action.payload
    },
    setMerchantContactList: (state, action) => {
      consoleLog('setMerchantContactList')
      if (action.payload.pageNumber === 0) {
        state.merchantContactList = [...action.payload.contactList]
      } else {
        state.merchantContactList = [...state.merchantContactList, ...action.payload.contactList]
      }
      state.hasMoreContact = action.payload.hasMoreContact
      state.contactPageNumber = action.payload.pageNumber
    },
    setBillingList: (state, action) => {
      state.billingList = action.payload
    },
    setAccountEditable: (state, action) => {
      state.isAccountEditable = action.payload
    },
    setProductSelected: (state, action) => {
      state.isProductAdded = action.payload
    },
    setMerchantAccountDetails: (state, action) => {
      state.merchantAccountDetails = action.payload
    },
    setOpportunityData: (state, action) => {
      state.opportunityData = action.payload
    },
    setPanDetails: (state, action) => {
      console.log('setPanDetails ' + JSON.stringify(action.payload))
      state.panDetails = action.payload
    },
    setCkycDetailsResult: (state, action) => {
      // state.ckycDetailsResult = action.payload
    },
    setCkycSearchResult: (state, action) => {
      state.ckycSearchResult = action.payload
    },
    setCkycGstResult: (state, action) => {
      state.ckycGstResult = action.payload
    },
    setCkysSearchCompleted: (state, action) => {
      state.ckycSearchCompleted = action.payload
    },
    obseleteCkycDetails: (state, action) => {
      // state.ckycDetailsResult = null
      state.ckycSearchResult = null
      state.ckycGstResult = null
      state.ckycSearchCompleted = false
    },
    clearData: (state, action) => {
      state.isAccountEditable = false
      state.billingList = []
      state.merchantContactMccList = null
      state.mccListOnAccount = []
      state.contactList = []
      state.merchantContactList = []
      state.contactPageNumber = 0
      state.hasMoreContact = true
      state.isProductAdded = false
      state.opportunityData = null
    },
  },
})

export const getMerchantMccContactList =
  (gqlQuery, isEditableQuery, acountId) => async (dispatch) => {
    const optyOwner = localStorage.getItem('opty_owner')
    const { data, error } = await gqlCaller({
      gqlQuery,
      dispatch,
      inputParams: {
        variables: {
          input: {
            id: acountId,
            ownerType: optyOwner,
          },
        },
        context: { isAuthRequired: true },
      },
      queryId: 500,
    })
    if (data?.merchantAccountDetails) {
      let result = data?.merchantAccountDetails

      const mccIds = data?.merchantAccountDetails?.merchantMccDetail.map((item) => {
        return item.id
      })

      const getEditableStatusForMcc = await checkEditableEntity(
        isEditableQuery,
        dispatch,
        mccIds,
        'accountMcc',
      )

      result = {
        ...result,
        merchantMccDetail: data?.merchantAccountDetails?.merchantMccDetail.map((item) => {
          const canEdit = getEditableStatusForMcc?.data?.editableIds?.editableIds.find((idItem) => {
            return idItem?.id === item?.id
          })
          consoleLog('canEdit == ' + JSON.stringify(canEdit))
          if (canEdit) {
            return { ...item, isEditable: canEdit.isEditable }
          }
          return item
        }),
      }
      dispatch(setMerchantContactMcc(result))
    }
  }

export const getMerchantMccContactListWithPagination =
  (gqlQuery, isEditableQuery, acountId, pageNumber, PAGE_SIZE = 10) =>
  async (dispatch) => {
    const { data, error } = await gqlCallerV2({
      gqlQuery,
      dispatch,
      input: {
        accountId: acountId,
        page: pageNumber,
        size: PAGE_SIZE,
      },
    })
    if (data?.contactList?.length) {
      let result = data?.contactList
      const hasMore = data?.contactList?.length === PAGE_SIZE

      const contactIds = data?.contactList.map((item) => {
        return item.id
      })

      const getEditableStatusForContact = await checkEditableEntity(
        isEditableQuery,
        dispatch,
        contactIds,
        'contact',
      )

      result = data?.contactList.map((item) => {
        const canEdit = getEditableStatusForContact?.data?.editableIds?.editableIds.find(
          (idItem) => {
            return idItem?.id === item?.id
          },
        )
        if (canEdit) {
          return { ...item, isEditable: canEdit.isEditable }
        }
        return item
      })
      dispatch(setMerchantContactList({ contactList: result, pageNumber, hasMoreContact: hasMore }))
    } else if (data?.contactList?.length === 0) {
      dispatch(setMerchantContactList({ contactList: [], pageNumber, hasMoreContact: false }))
    }
  }

export const getBillingList = (gqlQuery, isEditableQuery, acountId) => async (dispatch) => {
  const optyOwner = localStorage.getItem('opty_owner')
  const { data, error } = await gqlCaller({
    gqlQuery,
    dispatch,
    inputParams: {
      variables: {
        input: {
          accountId: acountId,
          allowEdit: true,
          limit: 50,
          offset: 0,
          ownerType: optyOwner,
        },
      },
      context: { isAuthRequired: true },
    },
    queryId: 500,
  })
  if (data?.getFilteredBillingEntities?.billingEntities) {
    const result = data?.getFilteredBillingEntities?.billingEntities.map((item) => {
      return {
        ...item,
        businessAddress: {
          ...item.businessAddress,
          pincode: item.businessAddress.pinCode,
          addressLine3: item.businessAddress.landmark,
        },
      }
    })
    dispatch(setBillingList(result))
  }
}

export const checkEditableEntity = async (query, dispatch, ids: any, entityName: string) => {
  const getEditableStatus = await gqlCaller({
    gqlQuery: query,
    dispatch,
    inputParams: {
      variables: {
        input: {
          entityName: entityName,
          ids: ids,
        },
      },
      context: { isAuthRequired: true },
    },
    queryId: 500,
  })
  return getEditableStatus
}

export const checkAccountEditStatus = (query, acountId) => async (dispatch) => {
  const arr = [acountId]
  const getEditableStatus = await checkEditableEntity(query, dispatch, arr, 'account')
  if (getEditableStatus?.data?.editableIds?.editableIds?.length > 0) {
    dispatch(setAccountEditable(getEditableStatus?.data?.editableIds?.editableIds[0].isEditable))
  } else {
    dispatch(setAccountEditable(false))
  }
}

/*
Using this function to check product is available, if product is there then as per OP-3879
don't allow user to edit MCC
*/
export const checkProductAdded = (gqlQuery, optyId) => async (dispatch) => {
  const { data } = await gqlCallerV2({
    gqlQuery,
    dispatch,
    input: {
      opportunityId: optyId,
    },
  })
  if (data) {
    const products = data?.getOpportunityProduct?.products
    dispatch(setProductSelected(products?.length > 0))
  }
}

export const {
  setContactList,
  setMccListOnAccount,
  setMerchantContactMcc,
  setMerchantContactList,
  setBillingList,
  setAccountEditable,
  setProductSelected,
  setOpportunityData,
  setMerchantAccountDetails,
  setPanDetails,
  setCkycDetailsResult,
  setCkycSearchResult,
  setCkycGstResult,
  setCkysSearchCompleted,
  obseleteCkycDetails,
  clearData,
} = businessSlice.actions

export const business = (state) => state.business

export default businessSlice.reducer
